import { useState } from "react";
import axios from "axios";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Reveal } from "./motion";
import { Swoosh } from "./ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "george@birdseyeviews.uk";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | done

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in every field.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("done");
      toast.success("Message sent — thanks, George will be in touch!");
      setForm({ name: "", email: "", message: "" });
      setTimeout(() => setStatus("idle"), 4000);
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong. Please email me directly.");
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 font-sans text-white placeholder:text-teal-200/40 outline-none transition-colors focus:border-emerald-400";

  return (
    <section id="contact" className="relative overflow-hidden bg-[#0B4F5C] py-24 lg:py-32">
      <Toaster position="top-center" richColors />
      <Swoosh className="-left-32 top-10 h-[460px] w-[460px] text-white/[0.05]" />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-2 lg:px-10">
        {/* Copy */}
        <div>
          <Reveal>
            <span className="font-sans text-xs font-semibold uppercase tracking-[0.3em] text-emerald-300">
              Get in touch
            </span>
            <h2 className="mt-4 font-serif text-5xl text-white sm:text-6xl">Contact</h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-8 space-y-5">
            <p className="font-sans text-base leading-relaxed text-teal-50/90">
              If you would like to know more, please email or use the contact form below.
            </p>
            <p className="font-sans text-base leading-relaxed text-teal-50/90">
              I currently cover Wiltshire and surrounding areas and can offer a no
              obligation quote to cover anything from simple raw footage, or edited
              films to include music, voice overs and titles. I am CAA compliant.
            </p>
            <p className="font-sans text-base leading-relaxed text-teal-50/90">
              As I am starting out, I won't charge sky high prices either.
            </p>
            <p className="font-serif text-2xl italic text-white">Hope to see you soon! George.</p>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-link"
              className="mt-4 inline-flex items-center gap-3 rounded-full border border-emerald-400/50 px-6 py-3 font-sans text-sm font-medium text-white transition-colors hover:bg-emerald-400 hover:text-slate-950"
            >
              <Mail className="h-4 w-4" />
              {EMAIL}
            </a>
          </Reveal>
        </div>

        {/* Form */}
        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-[2rem] border border-white/10 bg-black/20 p-8 backdrop-blur-sm sm:p-10"
          >
            <div className="space-y-5">
              <div>
                <label className="mb-2 block font-sans text-sm text-teal-100">Name</label>
                <input
                  data-testid="contact-name"
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your name"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-2 block font-sans text-sm text-teal-100">Email</label>
                <input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-2 block font-sans text-sm text-teal-100">Message</label>
                <textarea
                  data-testid="contact-message"
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell me about your project…"
                  className={`${inputCls} resize-none`}
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={status === "loading"}
                className="group flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 py-4 font-sans font-semibold text-slate-950 transition-all duration-300 hover:bg-emerald-400 disabled:opacity-70"
              >
                {status === "loading" ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</>
                ) : status === "done" ? (
                  <><CheckCircle2 className="h-5 w-5" /> Sent!</>
                ) : (
                  <><Send className="h-5 w-5 transition-transform group-hover:translate-x-1" /> Send message</>
                )}
              </button>
            </div>
          </form>
        </Reveal>
      </div>

      <div className="relative mx-auto mt-24 max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
          <span className="font-serif text-xl text-white">Birds Eye Views</span>
          <span className="font-sans text-xs uppercase tracking-[0.3em] text-teal-200/60">
            © {new Date().getFullYear()} · Drone Photography · CAA Compliant
          </span>
        </div>
      </div>
    </section>
  );
};
